import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ShieldCheck, Loader2, AlertTriangle, LogOut } from "lucide-react";
import { api } from "../api";
import { useAuth, roleHome } from "../AuthContext";
import Brand from "../../components/site/Brand";

/**
 * AcceptInvite is now session-aware:
 *
 *   • If you're already signed in as the same email → we don't try to
 *     re-activate; we just usher you into your workspace.
 *   • If you're signed in as a different email → we refuse to hijack
 *     the session and show a clear "wrong account" screen with Sign out.
 *   • If you're not signed in → normal flow (set password, activate).
 */
export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { acceptInvite, status, user, logout } = useAuth();

  const [state, setState] = useState({ status: "loading", detail: null, error: "" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setState({ status: "error", detail: null, error: "No invitation token provided." });
      return;
    }
    (async () => {
      try {
        const data = await api.get(`/auth/invite/${encodeURIComponent(token)}`);
        setState({ status: "ready", detail: data, error: "" });
      } catch (e) {
        setState({ status: "error", detail: null, error: e.message });
      }
    })();
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const data = await acceptInvite(token, password);
      navigate(roleHome(data?.user));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const inviteEmail = state.detail?.email?.toLowerCase() || "";
  const currentEmail = user?.email?.toLowerCase() || "";
  const alreadyMe = status === "authed" && currentEmail && currentEmail === inviteEmail;
  const wrongAccount = status === "authed" && currentEmail && inviteEmail && currentEmail !== inviteEmail;

  return (
    <div className="min-h-screen bg-[#F6F5F0] flex items-center justify-center px-5 py-10" data-testid="accept-invite-page">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <Brand tone="dark" />
        </div>

        <div className="bg-white rounded-2xl shadow-[0_20px_50px_-25px_rgba(11,18,32,0.25)] border border-slate-200 p-8">
          {(state.status === "loading" || status === "checking") && (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 size={14} className="animate-spin" /> Checking your invitation…
            </div>
          )}

          {state.status === "error" && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-rose-600 font-semibold">
                Invitation unavailable
              </div>
              <h1 className="mt-2 font-display text-2xl tracking-tight text-slate-900 font-semibold">
                We couldn't open this invite.
              </h1>
              <p className="mt-2 text-slate-500 text-[13.5px] leading-relaxed">{state.error}</p>
              <div className="mt-5">
                <Link
                  to="/app/login"
                  className="inline-flex items-center gap-2 rounded-lg bg-[#0B1220] text-white px-4 py-2.5 text-sm font-semibold"
                  data-testid="invite-error-go-login"
                >
                  Go to sign in <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          )}

          {state.status === "ready" && wrongAccount && (
            <div data-testid="invite-wrong-account">
              <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-amber-800 font-semibold">
                <AlertTriangle size={12} /> Wrong account
              </div>
              <h1 className="mt-2 font-display text-2xl tracking-tight text-slate-900 font-semibold">
                Sign out to accept this invite.
              </h1>
              <p className="mt-2 text-slate-500 text-[13.5px] leading-relaxed">
                You're signed in as <b className="text-slate-900">{user?.email}</b>, but this invitation is for
                {" "}<b className="text-slate-900">{state.detail.email}</b>. Sign out first, then re-open the invite link.
              </p>
              <button
                onClick={logout}
                data-testid="invite-wrong-account-signout"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B1220] hover:bg-[#131c31] text-white px-4 py-3 text-sm font-semibold"
              >
                <LogOut size={14} /> Sign out
              </button>
              <p className="mt-3 text-center text-[12px] text-slate-500">
                After signing out, reload this page to continue.
              </p>
            </div>
          )}

          {state.status === "ready" && alreadyMe && (
            <div data-testid="invite-already-me">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#8C6B33] font-semibold">
                You're already active
              </div>
              <h1 className="mt-2 font-display text-2xl tracking-tight text-slate-900 font-semibold">
                Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
              </h1>
              <p className="mt-2 text-slate-500 text-[13.5px] leading-relaxed">
                This invitation belongs to your current account.
              </p>
              <button
                onClick={() => navigate(roleHome(user))}
                data-testid="invite-already-me-continue"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B1220] hover:bg-[#131c31] text-white px-4 py-3 text-sm font-semibold"
              >
                Continue to your workspace <ArrowRight size={14} />
              </button>
            </div>
          )}

          {state.status === "ready" && !wrongAccount && !alreadyMe && (
            <>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#8C6B33] font-semibold">
                Invited to {state.detail.business_name}
              </div>
              <h1 className="mt-2 font-display text-2xl md:text-3xl tracking-tight text-slate-900 font-semibold">
                Welcome{state.detail.name ? `, ${state.detail.name.split(" ")[0]}` : ""}.
              </h1>
              <p className="mt-2 text-slate-500 text-sm">
                Set a password to activate your compliance workspace.
              </p>

              <dl className="mt-6 space-y-1.5 text-[12.5px]">
                <div className="flex justify-between border-b border-slate-100 pb-1.5">
                  <dt className="text-slate-500">Your email</dt>
                  <dd className="text-slate-900 font-medium" data-testid="invite-email">{state.detail.email}</dd>
                </div>
                <div className="flex justify-between border-b border-slate-100 pb-1.5">
                  <dt className="text-slate-500">Job title</dt>
                  <dd className="text-slate-900 font-medium">{state.detail.job_title || state.detail.role}</dd>
                </div>
                {state.detail.department && (
                  <div className="flex justify-between border-b border-slate-100 pb-1.5">
                    <dt className="text-slate-500">Department</dt>
                    <dd className="text-slate-900 font-medium">{state.detail.department}</dd>
                  </div>
                )}
              </dl>

              <form onSubmit={submit} className="mt-6 space-y-4">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Set a password</span>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="invite-password"
                    placeholder="At least 8 characters"
                    className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none transition-colors"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Confirm password</span>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="invite-confirm"
                    placeholder="Repeat your password"
                    className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none transition-colors"
                  />
                </label>

                {error && (
                  <div data-testid="invite-error" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  data-testid="invite-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B1220] hover:bg-[#131c31] disabled:opacity-40 text-white px-4 py-3 text-sm font-semibold transition-colors"
                >
                  {busy ? "Activating…" : "Activate my account"}
                  <ShieldCheck size={14} />
                </button>
              </form>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          Already activated?{" "}
          <Link to="/app/login" className="text-[#8C6B33] hover:text-[#0B1220] font-semibold">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
